import TheService from "@rws-framework/client/src/services/_service";
import { SITE_WIDE_ID, type AnnouncementMongo } from 'src/types/announcement';
import AnnouncementService, { type AnnouncementServiceInstance } from "./AnnouncementService";
import EventsService, { type EventsServiceInstance } from "./EventsService";
import JunctionUserService, { type JunctionUserServiceInstance } from "./JunctionUserService";
import FirebaseService, { type FirebaseServiceInstance } from "./FirebaseService";
import { StatefulSubscribable } from "../helpers/subscribable";

// Browsers store setTimeout's delay as a 32-bit signed integer; passing a value
// larger than 2^31-1 (~24.8 days) overflows and the callback runs *immediately*
// instead of waiting. We clamp scheduled transitions to that ceiling so a long-
// lived announcement (e.g. ends a month from now) doesn't get its end handler
// fired on the spot. Anything beyond this horizon falls back to "user refreshes".
const MAX_SETTIMEOUT_MS = 2 ** 31 - 1;

export type AnnouncementLiveState = {
  current: AnnouncementMongo | null;
  history?: AnnouncementMongo[]; // only set when isProfessor
};

export type AnnouncementSubscriber = (state: AnnouncementLiveState) => void;

/**
 * Live-state machine for the currently-displayable announcement.
 *
 * Owns: context resolution (course/role), fetching, firebase splicing,
 * boundary scheduling, and broadcasting to subscribers via `StatefulSubscribable`.
 *
 * The HTTP/CRUD surface lives in `AnnouncementService` — this service uses it
 * to fetch lists, but otherwise has no opinion on persistence.
 *
 * Push updates come from `FirebaseService` indirectly: we call its `connect` +
 * `subscribeToAnnouncementUpdates` to open the socket, but the two services
 * aren't wired by a direct callback. Firebase re-broadcasts each announcement
 * payload as a `jnct:firebaseservice:event` CustomEvent on the global
 * `document`, and we listen there (see `attachFirebaseListener`). The DOM is
 * the message bus — so any other part of the app can observe the same stream
 * without holding a reference to either service.
 *
 * Consumers subscribe directly: `service.subscribe(cb)`. Late joiners receive
 * the cached value immediately.
 */
class CurrentAnnouncementService extends TheService {
  static _IN_CLIENT: boolean = true;

  // Cached lists for the active course context.
  private siteWideAll: AnnouncementMongo[] = [];
  private allCatalogAnnouncements: AnnouncementMongo[] = [];

  // Subscription bookkeeping.
  private currentCatalogId: string | null = null;
  private isProfessor = false;
  private state = new StatefulSubscribable<AnnouncementLiveState>();
  private scheduledTimeouts: ReturnType<typeof setTimeout>[] = [];
  private fetchPromise: Promise<void> | null = null;
  private pendingSync = false;
  private firebaseAttached = false;
  private initialized = false;

  constructor(
    @AnnouncementService private announcementService: AnnouncementServiceInstance,
    @EventsService private eventsService: EventsServiceInstance,
    @JunctionUserService private junctionUserService: JunctionUserServiceInstance,
    @FirebaseService private firebaseService: FirebaseServiceInstance,
  ) {
    super();
  }

  /** Idempotent. Bind global listeners and trigger the first sync. Runs on
   *  first subscribe so the constructor stays side-effect-free and the class
   *  can be unit-tested without the DI container booting HTTP traffic. */
  private ensureInitialized(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.attachFirebaseListener();
    this.attachRouteListener();
    void this.sync();
  }

  public subscribe = (listener: AnnouncementSubscriber): (() => void) => {
    this.ensureInitialized();
    return this.state.subscribe(listener);
  };

  /** Establish the firebase socket and attach the announcement-update listener
   *  that dispatches `jnct:firebaseservice:event`. Runs once per session. */
  private async connectFirebase(): Promise<void> {
    if (this.firebaseAttached) return;
    const reseller = window.labelBundle?.reseller;
    if (!reseller) {
      console.warn("CurrentAnnouncementService: reseller not available, firebase not connected");
      return;
    }
    try {
      await this.firebaseService.connect(reseller);
      this.firebaseService.subscribeToAnnouncementUpdates();
      this.firebaseAttached = true;
    } catch (reason) {
      console.warn("Announcement firebase connect failed:", reason);
    }
  }

  /**
   * Resolve context, fetch lists, notify. Concurrent calls coalesce: if a
   * fetch is already in flight, we don't start a second one, but we remember
   * to re-sync as soon as it lands — otherwise a route change mid-fetch
   * would leave us serving the previous course's announcements until the
   * next firebase push.
   */
  private async sync(): Promise<void> {
    if (this.fetchPromise) {
      this.pendingSync = true;
      return this.fetchPromise;
    }
    this.fetchPromise = this.fetchAndNotify()
      .catch(err => { console.info('announcement sync failed:', err); })
      .finally(() => {
        this.fetchPromise = null;
        const shouldResync = this.pendingSync;
        this.pendingSync = false;
        if (shouldResync) void this.sync();
      });
    return this.fetchPromise;
  }

  private async fetchAndNotify(): Promise<void> {
    const { catalog_id, isProfessor } = await this.resolveContext();
    // resolveContext awaits getUser/getCourse, which means themes_service.init()
    // has finished and window.labelBundle.reseller is reliably set — safe to
    // attach the firebase listener now.
    await this.connectFirebase();
    this.currentCatalogId = catalog_id ?? null;
    this.isProfessor = isProfessor;
    try {
      const [siteWideAll, catalogAnnouncements] = await Promise.all([
        this.announcementService.get(SITE_WIDE_ID),
        catalog_id ? this.announcementService.get(catalog_id) : Promise.resolve([]),
      ]);
      this.siteWideAll = siteWideAll;
      this.allCatalogAnnouncements = catalogAnnouncements;
    } catch (error) {
      //console.error(error);
      return;
    }
    // If a resync was queued mid-fetch (route change, etc.), our data is
    // already stale — skip notify and let the queued sync deliver the
    // up-to-date state instead of flashing the old course's announcement.
    if (this.pendingSync) return;
    this.notify();
  }

  /** Resolve current catalog_id + role from app-wide state. No course → no role. */
  private async resolveContext(): Promise<{ catalog_id?: string; isProfessor: boolean }> {
    if (!window.stateParams?.courseId) return { isProfessor: false };
    try {
      const course = await this.eventsService.getCourse();
      const user = await this.junctionUserService.getUser();
      return {
        catalog_id: course.catalog_id,
        isProfessor: !!this.junctionUserService.hasProfessorRights(course, user),
      };
    } catch (err) {
      console.warn("CurrentAnnouncementService: resolveContext failed", err);
      return { isProfessor: false };
    }
  }

  /** Compute current/next from the cache, schedule transitions, deliver to all subscribers. */
  private notify(): void {
    const { current, next } = this.computeCurrentAndNext();
    this.rescheduleBoundaries(current, next);
    this.state.notify({
      current: current ?? null,
      history: this.computeHistory(),
    });
  }

  private computeCurrentAndNext() {
    const reachability: AnnouncementMongo['reachability'] = this.isProfessor ? 'Instructors' : 'Students';
    const catalog_id = this.currentCatalogId ?? SITE_WIDE_ID;
    return CurrentAnnouncementService.getCurrentAndNextAnnouncement(
      catalog_id, reachability, this.siteWideAll, this.allCatalogAnnouncements,
    );
  }

  /** Pure: pick the current + next announcement for a given context. */
  public static getCurrentAndNextAnnouncement(
    catalog_id: string,
    reachability: AnnouncementMongo['reachability'],
    allSiteWide: AnnouncementMongo[] = [],
    allCatalogAnnouncements: AnnouncementMongo[] = [],
  ): { current: AnnouncementMongo | undefined; next: AnnouncementMongo | undefined } {
    const now = new Date().toISOString();
    const current = pickCurrent(catalog_id, reachability, allSiteWide, allCatalogAnnouncements, now);
    const startedAfter = current?.start_date ?? now;
    const next = [...allSiteWide, ...allCatalogAnnouncements]
      .filter(ann => [reachability, 'Everyone'].includes(ann.reachability)
        && ann.start_date > startedAfter
        && !hasEnded(ann, now))
      .sort((a, b) => a.start_date.localeCompare(b.start_date))
      .at(0);
    return { current, next };
  }

  private rescheduleBoundaries(current?: AnnouncementMongo, next?: AnnouncementMongo): void {
    this.clearScheduled();
    if (current) this.scheduleNotifyAt(current.end_date);
    if (next) this.scheduleNotifyAt(next.start_date);
  }

  private computeHistory(): AnnouncementMongo[] | undefined {
    if (!this.isProfessor) return undefined;
    const now = new Date().toISOString();
    return this.allCatalogAnnouncements.filter(
      ann => ['Everyone', 'Instructors'].includes(ann.reachability) && hasStarted(ann, now),
    );
  }

  private scheduleNotifyAt(whenIso: string): void {
    const ms = new Date(whenIso).getTime() - Date.now();
    if (ms <= 0 || ms >= MAX_SETTIMEOUT_MS) return;
    this.scheduledTimeouts.push(setTimeout(() => { void this.notify(); }, ms));
  }

  private clearScheduled(): void {
    this.scheduledTimeouts.forEach(clearTimeout);
    this.scheduledTimeouts = [];
  }

  private attachRouteListener(): void {
    document.addEventListener('jnct:route:courseidchanged', () => { void this.sync(); });
  }

  private attachFirebaseListener(): void {
    document.documentElement.addEventListener(
      'jnct:firebaseservice:event',
      e => { void this.handleFirebaseEvent(e); },
    );
  }

  private async handleFirebaseEvent(
    e: HTMLElementEventMap['jnct:firebaseservice:event'],
  ): Promise<void> {
    if (e.detail.type !== 'all' || !e.detail.payload) return;
    if (this.fetchPromise) await this.fetchPromise;
    const announcement = e.detail.payload;

    const isSiteWide = !!announcement.catalogs?.some(c => c.id === SITE_WIDE_ID);
    const matchesCurrent = !!(this.currentCatalogId
      && announcement.catalogs?.some(c => c.id === this.currentCatalogId));
    if (!isSiteWide && !matchesCurrent) return;

    if (isSiteWide) {
      this.siteWideAll = upsert(this.siteWideAll, announcement);
    }
    if (matchesCurrent) {
      this.allCatalogAnnouncements = upsert(this.allCatalogAnnouncements, announcement);
    }
    this.notify();
  }
}

const hasStarted = (ann: AnnouncementMongo, at: string): boolean => ann.start_date <= at;
const hasEnded   = (ann: AnnouncementMongo, at: string): boolean => ann.end_date <= at;
const isLive     = (ann: AnnouncementMongo, at: string = new Date().toISOString()): boolean =>
  hasStarted(ann, at) && !hasEnded(ann, at);

function pickCurrent(
  catalog_id: string,
  reachability: AnnouncementMongo['reachability'],
  allSiteWide: AnnouncementMongo[],
  allCatalogAnnouncements: AnnouncementMongo[],
  now: string,
): AnnouncementMongo | undefined {
  const findFirstLive = (list: AnnouncementMongo[]) =>
    list.find(ann => ann.reachability === 'Everyone' && isLive(ann, now))
      ?? list.find(ann => ann.reachability === reachability && isLive(ann, now));

  const siteWideCurrent = findFirstLive(allSiteWide);
  if (siteWideCurrent || catalog_id === SITE_WIDE_ID) return siteWideCurrent;

  return findFirstLive(allCatalogAnnouncements);
}

function upsert(list: AnnouncementMongo[], announcement: AnnouncementMongo): AnnouncementMongo[] {
  if (announcement.deleted) return list.filter(a => a._id !== announcement._id);
  const idx = list.findIndex(a => a._id === announcement._id);
  // Replace in place when the announcement already exists, so firebase edits
  // don't reorder the list. `pickCurrent` uses `.find` on the cache and would
  // otherwise swap which announcement wins when two are simultaneously live.
  return idx === -1 ? [...list, announcement] : list.with(idx, announcement);
}

export default CurrentAnnouncementService.getSingleton()

export { CurrentAnnouncementService as CurrentAnnouncementServiceInstance }
